import type { SlaComplianceTrendPoint } from '@compass/client/types/cases_analytics';
import React, { useMemo, useState } from 'react';

import { ChartCard } from '@/pages/analytics/components/ChartCard';
import { ChartRenderBoundary } from '@/pages/analytics/components/ChartRenderBoundary';
import { ChartBodySkeleton } from '@/pages/analytics/components/ChartStates';
import { RateTrendChart } from '@/pages/analytics/components/RateTrendChart';
import {
  type ChartScope,
  OVERALL_BY_PROGRAM_OPTIONS,
  ScopeToggle,
} from '@/pages/analytics/components/ScopeToggle';
import type { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import type { TrendBucket } from '@/pages/analytics/types';
import { COLOR_POSITIVE } from '@/pages/analytics/utils/chartPalette';
import { normalizeSlaCompliance } from '@/pages/analytics/utils/normalizeChartRows';

interface SlaComplianceCardProps {
  data?: SlaComplianceTrendPoint[];
  trendBucket: TrendBucket;
  partialDates: Set<string>;
  colors: Map<string, string>;
  download: ReturnType<typeof useDownloadChart>;
  loading?: boolean;
}

const HEIGHT = 280;

export const SlaComplianceCard: React.FC<SlaComplianceCardProps> = ({
  data,
  trendBucket,
  partialDates,
  colors,
  download,
  loading,
}) => {
  const [scope, setScope] = useState<ChartScope>('overall');
  const points = useMemo(
    () =>
      (data ?? []).map(p => ({
        date: p.date ?? '',
        group: p.group ?? '',
        group_name: p.group_name ?? p.group ?? '',
        rate: p.rate ?? 0,
      })),
    [data],
  );

  return (
    <ChartCard
      title="SLA compliance over time"
      subtitle={`% of cases submitted within 24 business hours, per ${trendBucket} · dashed line = 100% target`}
      rightSlot={
        <ScopeToggle value={scope} onChange={setScope} options={OVERALL_BY_PROGRAM_OPTIONS} />
      }
      onDownload={(format, exportElement) =>
        data &&
        download(normalizeSlaCompliance(data), format, 'SLA compliance over time', exportElement)
      }
      downloadDisabled={!data}
    >
      {loading ? (
        <ChartBodySkeleton height={HEIGHT} />
      ) : (
        <ChartRenderBoundary resetKeys={[data, scope, trendBucket]} height={HEIGHT}>
          <RateTrendChart
            data={points}
            scope={scope}
            color={COLOR_POSITIVE}
            yMax={1}
            targetLine={1}
            trendBucket={trendBucket}
            partialDates={partialDates}
            colorByKey={colors}
            height={HEIGHT}
            seriesLabel="SLA compliance"
            gradient={false}
          />
        </ChartRenderBoundary>
      )}
    </ChartCard>
  );
};
